import { useMemo, type ReactNode } from 'react'
import { Icon } from '../../components/icons/Icon'
import { listProgramFilings } from '../../api/insights'
import { listCases } from '../../api/cases'
import { getCreditInstrumentByFiling } from '../../api/credit'
import { runExport, type ExportColumn } from '../../lib/export'
import { formatMoney, formatCount } from './format'
import { WidgetFrame } from './widgets/WidgetFrame'
import { WidgetErrorBoundary } from './widgets/WidgetErrorBoundary'
import { useWidgetData } from './widgets/useWidgetData'
import { useReportsSection } from './useReportsSection'
import {
  aggregateCaseStatusCounts,
  aggregateCertifiedByJurisdiction,
  aggregateTopClientsByCredit,
  type CaseStatusCount,
  type JurisdictionCreditTotal,
  type TopClientCredit,
} from './reportsData'
import type { CreditValueByJurisdictionResult, OutcomePathBucket } from '../../types/insights'
import type { ProgramFilingListView } from '../../types/programFiling'
import './charts/charts.css'
import './reports.css'

/** Every certified-value fact at launch is USD (same disclosed constant as `CreditValueByJurisdictionFyWidget`). */
const REPORTING_CURRENCY = 'USD'

/** Bounded, disclosed caps — this screen assembles its summary client-side from already-published
 * list endpoints (no new backend aggregation endpoint this story); each cap is surfaced honestly in
 * its card rather than silently truncating the numbers shown. */
const CASE_STATUS_SCAN_CAP = 200
const TOP_CLIENTS_FILING_SCAN_CAP = 50
const TOP_CLIENTS_LIMIT = 5

interface CaseStatusSectionData {
  counts: CaseStatusCount[]
  cappedAt: number | null
}

async function fetchCaseStatusSection(signal: AbortSignal): Promise<CaseStatusSectionData> {
  const page = await listCases({ limit: CASE_STATUS_SCAN_CAP }, signal)
  return {
    counts: aggregateCaseStatusCounts(page.items),
    cappedAt: page.nextCursor ? CASE_STATUS_SCAN_CAP : null,
  }
}

interface TopClientsSectionData {
  rows: TopClientCredit[]
  filingsScanned: number
  cappedAt: number | null
  unresolvedCount: number
}

async function fetchTopClientsSection(signal: AbortSignal): Promise<TopClientsSectionData> {
  const page = await listProgramFilings({ limit: TOP_CLIENTS_FILING_SCAN_CAP }, signal)
  const settled = await Promise.allSettled(
    page.items.map((filing: ProgramFilingListView) => getCreditInstrumentByFiling(filing.id, signal)),
  )
  let unresolvedCount = 0
  const instruments = settled.map((result) => {
    if (result.status === 'fulfilled') return result.value
    unresolvedCount += 1
    return null
  })
  return {
    rows: aggregateTopClientsByCredit(page.items, instruments, TOP_CLIENTS_LIMIT),
    filingsScanned: page.items.length,
    cappedAt: page.nextCursor ? TOP_CLIENTS_FILING_SCAN_CAP : null,
    unresolvedCount,
  }
}

interface TableColumn<T> {
  header: string
  align?: 'start' | 'end'
  render: (row: T) => ReactNode
}

function ReportTable<T>({ columns, rows, caption }: { columns: TableColumn<T>[]; rows: T[]; caption: string }) {
  return (
    <table className="iw-chart-table">
      <caption className="iw-visually-hidden">{caption}</caption>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.header} scope="col" style={{ textAlign: c.align === 'end' ? 'right' : 'left' }}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {columns.map((c) => (
              <td key={c.header} style={{ textAlign: c.align === 'end' ? 'right' : 'left' }}>
                {c.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const EMPTY_FILTER = {}

/**
 * `/insights/reports` — a portfolio summary assembled from already-published reads (credit-value-
 * by-jurisdiction-fy + monetization-outcomes widgets, `Case`, `ProgramFiling`, `CreditInstrument`).
 * FRONTEND-ONLY: no new backend aggregation endpoint this story — every card discloses its own
 * scan cap honestly (CLAUDE.md "keep exports honest") rather than presenting a partial figure as
 * if it were the whole tenant's total. Each card fetches/renders independently (same "one card's
 * failure never blanks the rest" posture as `DashboardScreen`).
 */
export function ReportsScreen() {
  // The jurisdiction/monetization cards ARE the real `insights` dashboard widgets (same endpoint,
  // same not_authorized/coming_soon/error semantics) — reused via the identical `useWidgetData`
  // hook `DashboardScreen` itself uses, not re-implemented.
  const jurisdiction = useWidgetData<CreditValueByJurisdictionResult>('credit-value-by-jurisdiction-fy', EMPTY_FILTER, false)
  const monetization = useWidgetData<OutcomePathBucket[]>('monetization-outcomes', EMPTY_FILTER, false)
  const caseStatus = useReportsSection<CaseStatusSectionData>(
    fetchCaseStatusSection,
    (data) => data.counts.length === 0,
  )
  const topClients = useReportsSection<TopClientsSectionData>(
    fetchTopClientsSection,
    (data) => data.rows.length === 0,
  )

  const jurisdictionTotals = useMemo<JurisdictionCreditTotal[]>(
    () => aggregateCertifiedByJurisdiction(Array.isArray(jurisdiction.data?.buckets) ? jurisdiction.data.buckets : []),
    [jurisdiction.data],
  )
  const monetizationBuckets = useMemo<OutcomePathBucket[]>(
    () => (Array.isArray(monetization.data) ? [...monetization.data].sort((a, b) => Number(b.sumValue) - Number(a.sumValue)) : []),
    [monetization.data],
  )

  const jurisdictionState = jurisdiction.status === 'ok' && jurisdictionTotals.length === 0 ? 'empty' : jurisdiction.status
  const monetizationState = monetization.status === 'ok' && monetizationBuckets.length === 0 ? 'empty' : monetization.status

  function handlePrint() {
    window.print()
  }

  return (
    <div className="iw-reports-print-area" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
      <div
        className="iw-reports-noprint"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--iw-space-3)',
          flexWrap: 'wrap',
          padding: 'var(--iw-space-4) var(--iw-space-5)',
          borderBottom: '1px solid var(--iw-border)',
          background: 'var(--iw-surface)',
        }}
      >
        <span style={{ color: 'var(--iw-accent)', display: 'inline-flex' }}>
          <Icon name="printer" size={20} />
        </span>
        <h1 style={{ fontSize: 'var(--iw-size-lg)', margin: 0 }}>Reports</h1>
        <span style={{ flex: 1 }} />
        <button type="button" className="iw-btn iw-btn--sm" onClick={handlePrint}>
          <Icon name="printer" size={14} />
          Print / Save as PDF
        </button>
      </div>

      <div className="iw-reports-print-header" style={{ display: 'none' }}>
        <h1>IncentiWise — Portfolio summary</h1>
      </div>

      <div className="iw-dashboard-grid">
        <WidgetErrorBoundary title="Credits certified by jurisdiction" icon="bar-chart">
          <WidgetFrame
            title="Credits certified by jurisdiction"
            icon="bar-chart"
            state={jurisdictionState}
            message={jurisdiction.message}
            onRetry={jurisdiction.retry}
            exportAction={
              jurisdictionTotals.length > 0
                ? {
                    onClick: () =>
                      runExport<JurisdictionCreditTotal>(
                        'csv',
                        'Credits certified by jurisdiction',
                        jurisdictionTotals,
                        [
                          { id: 'jurisdiction', header: 'Jurisdiction', toText: (r) => r.label },
                          { id: 'total', header: `Certified total (${REPORTING_CURRENCY})`, toText: (r) => formatMoney(String(r.total), REPORTING_CURRENCY) },
                        ] satisfies ExportColumn<JurisdictionCreditTotal>[],
                      ),
                  }
                : undefined
            }
          >
            <ReportTable<JurisdictionCreditTotal>
              caption="Credits certified by jurisdiction"
              rows={jurisdictionTotals}
              columns={[
                { header: 'Jurisdiction', render: (r) => r.label },
                { header: `Certified total (${REPORTING_CURRENCY})`, align: 'end', render: (r) => formatMoney(String(r.total), REPORTING_CURRENCY) },
              ]}
            />
          </WidgetFrame>
        </WidgetErrorBoundary>

        <WidgetErrorBoundary title="Credits by monetization outcome" icon="landmark">
          <WidgetFrame
            title="Credits by monetization outcome"
            icon="landmark"
            state={monetizationState}
            message={monetization.message}
            onRetry={monetization.retry}
            exportAction={
              monetizationBuckets.length > 0
                ? {
                    onClick: () =>
                      runExport<OutcomePathBucket>('csv', 'Credits by monetization outcome', monetizationBuckets, [
                        { id: 'label', header: 'Outcome', toText: (b) => b.label },
                        { id: 'count', header: 'Filings', toText: (b) => formatCount(b.count) },
                        { id: 'sumValue', header: 'Value', toText: (b) => formatMoney(b.sumValue, b.currency ?? 'USD') },
                      ]),
                  }
                : undefined
            }
          >
            <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: '0 0 var(--iw-space-2)' }}>
              Includes transferred, direct-pay, and carryforward outcomes (config-not-code paths — labels come from the
              backend).
            </p>
            <ReportTable<OutcomePathBucket>
              caption="Credits by monetization outcome"
              rows={monetizationBuckets}
              columns={[
                { header: 'Outcome', render: (b) => b.label },
                { header: 'Filings', align: 'end', render: (b) => formatCount(b.count) },
                { header: 'Value', align: 'end', render: (b) => formatMoney(b.sumValue, b.currency ?? 'USD') },
              ]}
            />
          </WidgetFrame>
        </WidgetErrorBoundary>

        <WidgetErrorBoundary title="Cases by status" icon="briefcase">
          <WidgetFrame
            title="Cases by status"
            icon="briefcase"
            state={caseStatus.status}
            message={caseStatus.message}
            onRetry={caseStatus.retry}
            exportAction={
              caseStatus.data && caseStatus.data.counts.length > 0
                ? {
                    onClick: () =>
                      runExport<CaseStatusCount>('csv', 'Cases by status', caseStatus.data!.counts, [
                        { id: 'status', header: 'Status', toText: (r) => r.label },
                        { id: 'count', header: 'Cases', toText: (r) => formatCount(r.count) },
                      ]),
                  }
                : undefined
            }
          >
            {caseStatus.data ? (
              <>
                <ReportTable<CaseStatusCount>
                  caption="Cases by status"
                  rows={caseStatus.data.counts}
                  columns={[
                    { header: 'Status', render: (r) => r.label },
                    { header: 'Cases', align: 'end', render: (r) => formatCount(r.count) },
                  ]}
                />
                {caseStatus.data.cappedAt ? (
                  <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
                    Counts based on the first {caseStatus.data.cappedAt} cases returned — more cases exist, so this is a lower
                    bound, not the full tenant total.
                  </p>
                ) : null}
              </>
            ) : null}
          </WidgetFrame>
        </WidgetErrorBoundary>

        <WidgetErrorBoundary title="Top clients by certified credit" icon="dollar-sign">
          <WidgetFrame
            title="Top clients by certified credit"
            icon="dollar-sign"
            state={topClients.status}
            message={topClients.message}
            onRetry={topClients.retry}
            exportAction={
              topClients.data && topClients.data.rows.length > 0
                ? {
                    onClick: () =>
                      runExport<TopClientCredit>('csv', 'Top clients by certified credit', topClients.data!.rows, [
                        { id: 'accountName', header: 'Client', toText: (r) => r.accountName },
                        { id: 'certifiedTotal', header: 'Certified total', toText: (r) => formatMoney(String(r.certifiedTotal), r.currency) },
                      ]),
                  }
                : undefined
            }
          >
            {topClients.data ? (
              <>
                <ReportTable<TopClientCredit>
                  caption="Top clients by certified credit"
                  rows={topClients.data.rows}
                  columns={[
                    { header: 'Client', render: (r) => r.accountName },
                    { header: 'Certified total', align: 'end', render: (r) => formatMoney(String(r.certifiedTotal), r.currency) },
                  ]}
                />
                <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
                  Computed from the first {topClients.data.filingsScanned} filing{topClients.data.filingsScanned === 1 ? '' : 's'} returned by the
                  API{topClients.data.cappedAt ? ` (more filings exist — this ranking may be incomplete)` : ''}
                  {topClients.data.unresolvedCount > 0
                    ? `; ${topClients.data.unresolvedCount} filing${topClients.data.unresolvedCount === 1 ? '' : 's'} could not be resolved and were excluded`
                    : ''}
                  . Amounts are never summed across currencies.
                </p>
              </>
            ) : null}
          </WidgetFrame>
        </WidgetErrorBoundary>
      </div>
    </div>
  )
}
